/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// react
import { useEffect, useState } from 'react';

// mantine
import {
	UnstyledButton,
	Title,
	Image,
	Pill,
	Stack,
	Tooltip,
} from '@mantine/core';

// icons
import CollapsedLogoIcon from '@assets/images/collapsed-logo.svg';
import ArecWhiteLogoIcon from '@assets/icons/arec-white-logo.svg';

// styles
import classes from './Sidebar.module.css';
import clsx from 'clsx';

// constants
import {
	NOT_APPLICABLE,
	LOCAL,
	STAGING,
	DEMO,
	DEVELOP,
} from '@config/constants/common';

// i18n
import { useTranslation } from 'react-i18next';
import {
	IS_DEMO,
	IS_DEVELOP,
	IS_LOCAL,
	IS_PRODUCTION,
	IS_STAGING,
} from '@config/constants/env';
import { useMediaQuery } from '@mantine/hooks';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type SidebarItemProps = {
	id: string;
	active?: boolean;
	icon: React.ReactNode;
	iconInactive: React.ReactNode;
	text: string;
	path?: string;
	subMenu?: SubMenuItemProps[] | null;
	disabled?: boolean;
	group: string;
	visible?: boolean;
};

type SubMenuItemProps = Omit<
	SidebarItemProps,
	'subMenu'
> & {
	iconInactive: React.ReactNode;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export function Sidebar(props: {
	onChange: (link: SidebarItemProps) => void;
	sidebarItems: any[];
	activeModule?: string;
	isArecPartner?: boolean;
}) {
	const { sidebarItems } = props;
	const { t } = useTranslation();
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	/* ********************************** CONSTANTS ********************************* */
	const groupedItems = sidebarItems.reduce(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(acc: any, item) => {
			if (item.group) {
				if (!acc[item.group]) {
					acc[item.group] = [];
				}
				acc[item.group].push(item);
			}

			return acc;
		},
		{}
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [selectedItem, setSelectedItem] = useState<any>(
		{}
	);

	const [hoveredItem, setHoveredItem] = useState<
		Partial<SidebarItemProps>
	>({});

	const [visible, setVisible] = useState(false);

	const handleSidebarMouseLeave = () => {
		setVisible(false);
	};

	const handleMenuClick = (link: SidebarItemProps) => {
		if (!Array.isArray(link.subMenu)) {
			setSelectedItem(link);
			props.onChange(link);
		}
	};

	const handleMenuMouseLeave = (
		link: SidebarItemProps
	) => {
		setHoveredItem(link);
		setVisible(true);
	};

	const findMenuItemBySubMenuId = (
		itemId: string,
		sidebarItems: SidebarItemProps[]
	) => {
		const item = sidebarItems.find(
			(item: SidebarItemProps) =>
				item?.subMenu?.some(
					(subItem: SubMenuItemProps) =>
						subItem.id === itemId
				)
		);

		if (!item) {
			const menuFound = sidebarItems.find(
				(item: SidebarItemProps) => item.id === itemId
			);

			if (menuFound) {
				return {
					...menuFound,
					active: true,
				};
			}
		}

		if (!item || !item.subMenu) return undefined;

		return item
			? {
					...item,
					active: true,
					subMenu: item.subMenu.map(
						(subItem: SubMenuItemProps) =>
							subItem.id === itemId
								? { ...subItem, active: true }
								: subItem
					),
				}
			: undefined;
	};

	const isSubMenuActiveInItem = (
		subMenuId: string,
		sidebarItem: SidebarItemProps
	) => {
		return (
			sidebarItem?.subMenu?.some(
				(subItem: SubMenuItemProps) =>
					subItem.id === subMenuId && subItem.active
			) || false
		);
	};

	const handleSubMenuClick = (
		subMenu: SubMenuItemProps
	) => {
		const findItem: SidebarItemProps | undefined =
			findMenuItemBySubMenuId(subMenu.id, sidebarItems);
		setSelectedItem(findItem);
		props.onChange(subMenu);
	};

	useEffect(() => {
		if (props.activeModule) {
			const foundMenu = findMenuItemBySubMenuId(
				props.activeModule,
				sidebarItems
			);

			setSelectedItem(foundMenu);
		}
	}, [props.activeModule]);

	return (
		<nav className={classes.navbar}>
			<div
				className={classes.wrapper}
				onMouseLeave={handleSidebarMouseLeave}
			>
				{/* Section Menus */}
				<div
					className={clsx(
						classes.aside,
						props.isArecPartner && classes.asidePartner,
						IS_STAGING &&
							!IS_PRODUCTION &&
							classes.bottomBorderStaging,
						IS_LOCAL &&
							!IS_PRODUCTION &&
							classes.bottomBorderLocal,
						IS_DEVELOP &&
							!IS_PRODUCTION &&
							classes.bottomBorderDevelop,
						IS_DEMO &&
							!IS_PRODUCTION &&
							classes.bottomBorderDemo
					)}
				>
					{/* Logo */}
					<div className={classes.logo}>
						<Stack
							justify="flex-start"
							align="flex-end"
							gap={'5px'}
						>
							{props.isArecPartner ? (
								<Image
									fit="contain"
									src={ArecWhiteLogoIcon}
									h={'50px'}
									w={'50px'}
									mt={'10px'}
								/>
							) : (
								<Image
									fit="contain"
									src={CollapsedLogoIcon}
									className={classes.logoClosed}
								/>
							)}
							{IS_DEVELOP && !IS_PRODUCTION && (
								<Pill
									className={clsx(
										classes.pillDevelopClosed
									)}
									size="xs"
								>
									{DEVELOP}
								</Pill>
							)}
							{IS_DEMO && !IS_PRODUCTION && (
								<Pill
									className={clsx(classes.pillDemoClosed)}
									size="xs"
								>
									{DEMO}
								</Pill>
							)}
							{IS_STAGING && !IS_PRODUCTION && (
								<Pill
									className={clsx(
										classes.pillStagingClosed
									)}
									size="xs"
								>
									{STAGING}
								</Pill>
							)}
							{IS_LOCAL && !IS_PRODUCTION && (
								<Pill
									className={clsx(
										classes.pillLocalClosed
									)}
									size="xs"
								>
									{LOCAL}
								</Pill>
							)}
						</Stack>
					</div>
					<div
						className={clsx(
							props.isArecPartner
								? classes.dividerArecLogo
								: classes.dividerLogo
						)}
					/>
					{/* Menus */}
					<div className={classes.content}>
						{Array.isArray(Object.keys(groupedItems)) &&
							Object.keys(groupedItems)
								.filter(
									(item) => item !== 'sidebar.others'
								)
								.map((item, index) => (
									<div key={index}>
										<div
											className={classes.containerGroup}
										>
											<span className={classes.groupText}>
												{t(item)}
											</span>
											{groupedItems[item] &&
												Array.isArray(
													groupedItems[item]
												) &&
												groupedItems[item].length > 0 &&
												groupedItems[item].map((menu) => (
													<Tooltip
														offset={{
															mainAxis: isBigScreen
																? 30
																: 33,
															crossAxis: 0,
														}}
														color="var(--mantine-color-dark-blue)"
														disabled={Array.isArray(
															menu.subMenu
														)}
														position="right"
														withArrow
														transitionProps={{
															transition: 'fade-left',
															duration: 300,
														}}
														key={menu.id}
														label={t(menu.text)}
													>
														<UnstyledButton
															data-hovered={
																hoveredItem.id ==
																	menu.id &&
																hoveredItem &&
																hoveredItem.subMenu &&
																Array.isArray(
																	hoveredItem.subMenu
																) &&
																hoveredItem.subMenu
																	.length > 0 &&
																visible
																	? true
																	: undefined
															}
															disabled={Boolean(
																menu.disabled
															)}
															key={menu.id}
															onClick={() => {
																handleMenuClick(menu);
															}}
															onMouseEnter={() => {
																handleMenuMouseLeave(
																	menu
																);
															}}
															className={classes.mainLink}
															data-disabled={
																menu.disabled
																	? true
																	: undefined
															}
															data-active={
																menu.id ==
																(selectedItem &&
																	selectedItem.id)
																	? true
																	: undefined
															}
														>
															<img
																src={
																	menu.id ==
																	(selectedItem &&
																		selectedItem.id)
																		? menu.icon
																		: menu.iconInactive
																}
																className={
																	classes.iconMenu
																}
															/>
														</UnstyledButton>
													</Tooltip>
												))}
										</div>
										<div className={classes.divider} />
									</div>
								))}
					</div>
					{/* sidebar.otherss */}
					<div className={classes.containerBottom}>
						{Array.isArray(
							Object.keys(groupedItems).filter(
								(item) => item == 'sidebar.others'
							)
						) &&
							Object.keys(groupedItems)
								.filter(
									(item) => item == 'sidebar.others'
								)
								.map((item, index) => (
									<div key={index}>
										<div
											className={classes.containerGroup}
										>
											<span className={classes.groupText}>
												{t(item)}
											</span>
											{groupedItems[item] &&
												Array.isArray(
													groupedItems[item]
												) &&
												groupedItems[item].length > 0 &&
												groupedItems[item].map((menu) => (
													<Tooltip
														offset={{
															mainAxis: isBigScreen
																? 30
																: 33,
															crossAxis: 0,
														}}
														color="var(--mantine-color-dark-blue)"
														disabled={Array.isArray(
															menu.subMenu
														)}
														position="right"
														withArrow
														transitionProps={{
															transition: 'fade-left',
															duration: 300,
														}}
														key={menu.id}
														label={t(menu.text)}
													>
														<UnstyledButton
															disabled={menu.disabled}
															onClick={() => {
																handleMenuClick(menu);
															}}
															onMouseEnter={() => {
																handleMenuMouseLeave(
																	menu
																);
															}}
															className={classes.mainLink}
															data-disabled={
																menu.disabled || undefined
															}
															data-active={
																menu.id ==
																	(selectedItem &&
																		selectedItem.id) &&
																selectedItem.id !==
																	'logout'
																	? true
																	: undefined
															}
														>
															<img
																src={
																	menu.id ==
																		(selectedItem &&
																			selectedItem.id) &&
																	selectedItem.id !==
																		'logout'
																		? menu.icon
																		: menu.iconInactive
																}
																className={
																	classes.iconMenu
																}
															/>
														</UnstyledButton>
													</Tooltip>
												))}
										</div>
									</div>
								))}
					</div>
					{props.isArecPartner && (
						<Image
							mt={'15px'}
							fit="contain"
							src={CollapsedLogoIcon}
							className={classes.logoClosed}
						/>
					)}
				</div>

				{/* Section SubMenus */}
				{hoveredItem &&
					hoveredItem.subMenu &&
					Array.isArray(hoveredItem.subMenu) &&
					hoveredItem.subMenu.length > 0 && (
						<div
							className={clsx(
								classes.main,
								visible && classes.mainActive
							)}
						>
							<div className={classes.containerTitle}>
								<div className={classes.wrapperTitle}>
									<Title
										order={4}
										className={classes.title}
									>
										{hoveredItem && hoveredItem.text
											? t(hoveredItem.text)
											: NOT_APPLICABLE}
									</Title>
								</div>
							</div>
							<div className={classes.contentLinks}>
								{hoveredItem &&
									hoveredItem.subMenu &&
									hoveredItem.subMenu.map(
										(subMenu: SubMenuItemProps) => (
											<a
												className={classes.link}
												data-active={
													isSubMenuActiveInItem(
														subMenu.id,
														selectedItem
													) || undefined
												}
												data-disabled={
													subMenu.disabled || undefined
												}
												onClick={(event) => {
													event.preventDefault();
													if (!subMenu.disabled)
														handleSubMenuClick(subMenu);
												}}
												key={subMenu.id}
											>
												{subMenu.icon && (
													<Image
														src={
															isSubMenuActiveInItem(
																subMenu.id,
																selectedItem
															)
																? subMenu.icon
																: subMenu.iconInactive
														}
														className={
															classes.iconSubMenu
														}
													/>
												)}
												{t(subMenu.text)}
											</a>
										)
									)}
							</div>
						</div>
					)}
			</div>
		</nav>
	);
}
